import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import s from './OnboardPage.module.css';

/** Extended domain info returned during onboarding with DNS setup details. */
interface OnboardDomain {
  id: string;
  domain: string;
  ruaAddress: string;
  dnsRecord: string;
}

/* -------------------------------------------------------------------------- */
/*  Step indicator                                                             */
/* -------------------------------------------------------------------------- */

function StepIndicator({ current }: { current: number }) {
  const steps = [1, 2, 3];
  return (
    <div className={s.stepIndicator}>
      {steps.map((step, i) => (
        <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {i > 0 && <div className={s.dotSeparator} />}
          <div
            className={
              step === current
                ? s.dotActive
                : step < current
                  ? s.dotDone
                  : s.dot
            }
          />
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1: Name your organization                                             */
/* -------------------------------------------------------------------------- */

function Step1({
  onNext,
}: {
  onNext: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Organization name is required.');
      return;
    }

    setLoading(true);
    try {
      await api.onboarding.createTenant(name.trim());
      onNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.card}>
      <h1 className={s.heading}>Name your organization</h1>
      <p className={s.subtext}>
        This is the workspace where your domains and reports will live.
      </p>

      <form onSubmit={handleSubmit}>
        {error && <div className={s.error}>{error}</div>}

        <div className={s.field}>
          <label htmlFor="org-name" className={s.label}>Organization name</label>
          <input
            id="org-name"
            type="text"
            className={s.input}
            placeholder="Acme Corp"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div className={s.actions}>
          <button type="submit" className={s.btnAccent} disabled={loading}>
            {loading ? 'Creating\u2026' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2: Add your first domain                                              */
/* -------------------------------------------------------------------------- */

function Step2({
  onNext,
}: {
  onNext: () => void;
}) {
  const [domainInput, setDomainInput] = useState('');
  const [domainData, setDomainData] = useState<OnboardDomain | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleAddDomain(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const value = domainInput.trim().toLowerCase();
    if (!value) {
      setError('Enter a domain name.');
      return;
    }

    setLoading(true);
    try {
      const result = await api.domains.create(value);
      // The API returns { domain }, which during onboarding includes setup info
      const d = result.domain;
      const ruaAddress = `${d.id}@reports.dmarcopolo.com`;
      const dnsRecord = `_dmarc.${value} TXT "v=DMARC1; p=none; rua=mailto:${ruaAddress}"`;
      setDomainData({
        id: d.id,
        domain: d.domain,
        ruaAddress,
        dnsRecord,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!domainData) return;
    navigator.clipboard.writeText(domainData.dnsRecord).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={s.card}>
      <h1 className={s.heading}>Add your first domain</h1>
      <p className={s.subtext}>
        Enter the domain you want to monitor for DMARC reports.
      </p>

      {error && <div className={s.error}>{error}</div>}

      {!domainData ? (
        <form onSubmit={handleAddDomain}>
          <div className={s.field}>
            <label htmlFor="domain" className={s.label}>Domain</label>
            <input
              id="domain"
              type="text"
              className={s.input}
              placeholder="example.com"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className={s.actions}>
            <button type="submit" className={s.btnAccent} disabled={loading}>
              {loading ? 'Adding\u2026' : 'Add Domain'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className={s.domainAdded}>
            Domain added: {domainData.domain}
          </div>

          <div className={s.dnsSection}>
            <div className={s.dnsLabel}>Reporting address</div>
            <div className={s.ruaAddress}>{domainData.ruaAddress}</div>
          </div>

          <div className={s.dnsSection}>
            <div className={s.dnsLabel}>Add this DNS TXT record</div>
            <div className={s.dnsRecord}>
              <button
                type="button"
                className={s.copyBtn}
                onClick={handleCopy}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              {domainData.dnsRecord}
            </div>
          </div>

          <div className={s.actions}>
            <button type="button" className={s.btnAccent} onClick={onNext}>
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3: Done                                                               */
/* -------------------------------------------------------------------------- */

function Step3() {
  const navigate = useNavigate();

  return (
    <div className={s.card}>
      <div className={s.doneIcon}>&#10003;</div>
      <h1 className={s.heading}>You're all set</h1>
      <p className={s.subtext}>
        Your domain is configured and waiting for reports. Once mailbox
        providers start sending DMARC aggregate reports, they'll appear in
        your dashboard automatically.
      </p>
      <p className={s.doneNote}>
        Reports typically arrive within 24&ndash;48 hours.
      </p>

      <div className={s.actions}>
        <button
          type="button"
          className={s.btnAccent}
          onClick={() => navigate('/app', { replace: true })}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  OnboardPage                                                                */
/* -------------------------------------------------------------------------- */

export default function OnboardPage() {
  const [step, setStep] = useState(1);

  return (
    <div className={s.page}>
      <div className={s.container}>
        <StepIndicator current={step} />

        {step === 1 && <Step1 onNext={() => setStep(2)} />}
        {step === 2 && <Step2 onNext={() => setStep(3)} />}
        {step === 3 && <Step3 />}
      </div>
    </div>
  );
}
